import React, {memo, useMemo} from 'react'

import {useBooleanQueryParamState, useSyncedBooleanQueryParam} from '../../../../../hooks/routes'
import {useBuildData} from '../../../../../hooks/useEntity'
import useEventCallback from '../../../../../hooks/useEventCallback'
import type {BuildId} from '../../../../../types'
import {UserProperties} from '../../../../../utils/userProperties'
import Loader from '../../../../common/Loader/Loader'
import {ShowDependenciesCheckbox} from '../../../../packages/Parameters/components/FromDependenciesCheckbox/ShowDependenciesCheckbox'
import {ParametersModifyCheckbox} from '../../../../packages/Parameters/components/ModifyCheckbox/ParametersModifyCheckbox'
import {ParametersPageHeader} from '../../../../packages/Parameters/components/PageHeader/ParametersPageHeader'
import {ParametersTree} from '../../../../packages/Parameters/components/ParametersTree/ParametersTree'
import {ParametersSearch} from '../../../../packages/Parameters/components/Search/ParametersSearch'
import {useSearchState} from '../../../../packages/Parameters/hooks/useSearchState'
import {ParameterCategory, ParameterGroup} from '../../../../packages/Parameters/Parameters.types'
import {useQueryMergedBuildParameters} from '../hooks/useQueryMergedBuildParameters'

import styles from './BuildParameters.css'

import {inlineListMaxLength} from 'src/components/packages/Parameters/Parameters.constants'
import {filterParameters} from 'src/components/packages/Parameters/utils/filterParameters'
import {groupParameters} from 'src/components/packages/Parameters/utils/groupParameters'

interface Props {
  buildId: BuildId
  search?: string
}

function BuildParameterComponent(props: Props) {
  const {buildId} = props

  const {search, deferredSearch, handleChangeSearch, handleClearSearch} = useSearchState(
    props.search,
  )

  const [showFromDependencies, setShowFromDependencies] = useSyncedBooleanQueryParam({
    param: UserProperties.BUILD_PARAMETERS_SHOW_DEPENDENCIES,
    userProperty: UserProperties.BUILD_PARAMETERS_SHOW_DEPENDENCIES,
  })

  const [modifyOnly, setModifyOnly] = useBooleanQueryParamState('onlyModified')

  const {buildParameters, dependenciesParameters, dependenciesTotalCount} =
    useQueryMergedBuildParameters({
      buildId,
      search: deferredSearch,
      showFromDependencies,
    })

  const userDefinedParameters = useMemo(
    () => ({
      category: ParameterCategory.USER_DEFINED,
      original: buildParameters.originalProperties,
      groups: groupParameters(
        filterParameters({
          parameters: buildParameters.properties?.property ?? [],
          search: deferredSearch,
          modifyOnly,
          original: buildParameters.originalProperties,
        }),
      ),
    }),
    [
      buildParameters.originalProperties,
      buildParameters.properties?.property,
      modifyOnly,
      deferredSearch,
    ],
  )

  const dependencies = useMemo(
    () => ({
      group: ParameterGroup.DEPENDENCIES,
      items: filterParameters({
        parameters: dependenciesParameters.resultingProperties?.property ?? [],
        search: deferredSearch,
        modifyOnly,
        original: dependenciesParameters.startProperties,
      }),
    }),
    [
      dependenciesParameters.resultingProperties?.property,
      modifyOnly,
      deferredSearch,
      dependenciesParameters.startProperties,
    ],
  )

  const actualOriginal = useMemo(
    () => ({
      ...buildParameters.startProperties,
      ...dependenciesParameters.startProperties,
    }),
    [buildParameters.startProperties, dependenciesParameters.startProperties],
  )

  const composite = useBuildData({buildId}, build => build?.composite)
  const parameters = useMemo(
    () => [
      userDefinedParameters,
      {
        category: composite ? ParameterCategory.ACTUAL : ParameterCategory.ACTUAL_ON_AGENT,
        original: actualOriginal,
        groups: [
          ...groupParameters(
            filterParameters({
              parameters: buildParameters.resultingProperties?.property ?? [],
              search: deferredSearch,
              modifyOnly,
              original: buildParameters.startProperties,
            }),
          ),
          ...(showFromDependencies ? [dependencies] : []),
        ],
      },
    ],
    [
      userDefinedParameters,
      actualOriginal,
      buildParameters.resultingProperties?.property,
      buildParameters.startProperties,
      deferredSearch,
      modifyOnly,
      showFromDependencies,
      dependencies,

      composite,
    ],
  )

  const handleChangeModifyOnly = useEventCallback(() => {
    setModifyOnly(!modifyOnly)
  })

  const handleChangeShowFromDependencies = useEventCallback(() => {
    setShowFromDependencies(!showFromDependencies)
  })

  const totalLength =
    (buildParameters.resultingProperties?.count ?? 0) +
    (buildParameters.properties?.count ?? 0) +
    (showFromDependencies ? dependenciesParameters.resultingProperties?.count ?? 0 : 0)

  const isVirtualized = totalLength > inlineListMaxLength

  const count = useMemo(() => {
    if (deferredSearch === '') {
      return undefined
    }

    if (modifyOnly) {
      return 0
    }

    if (dependenciesParameters.isClientSearch) {
      return dependencies.items.length
    }

    return dependenciesParameters?.resultingProperties?.count
  }, [
    deferredSearch,
    dependencies.items.length,
    dependenciesParameters.isClientSearch,
    dependenciesParameters?.resultingProperties?.count,
    modifyOnly,
  ])

  return (
    <>
      <ParametersPageHeader>
        <ParametersSearch
          className={styles.search}
          value={search}
          onChange={handleChangeSearch}
          onClear={handleClearSearch}
        />
        <ParametersModifyCheckbox checked={modifyOnly} onChange={handleChangeModifyOnly} />

        {dependenciesTotalCount > 0 && (
          <ShowDependenciesCheckbox
            checked={showFromDependencies}
            isLoading={dependenciesParameters.isLoading}
            count={count}
            onChange={handleChangeShowFromDependencies}
          />
        )}
      </ParametersPageHeader>

      {buildParameters.isLoading ? (
        <Loader />
      ) : (
        <ParametersTree
          highlight={deferredSearch}
          parameters={parameters}
          isVirtualized={isVirtualized}
        />
      )}
    </>
  )
}

export const BuildParameters = memo<Props>(BuildParameterComponent)
